import { Avatar, AvatarFallback, AvatarImage } from '@workspace/ui/components/avatar'
import { cn, getInitials } from '@workspace/ui/lib/utils'
import { Camera, Loader2 } from 'lucide-react'
import * as React from 'react'

export interface ProfileHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Cover image URL */
  coverImageUrl?: string | null
  /** Avatar/logo image URL */
  avatarUrl?: string | null
  /** Profile name/title */
  name: string
  /** Subtitle/category text */
  subtitle?: string | null
  /** Badge elements to display next to title */
  badges?: React.ReactNode
  /** Action buttons (Edit, Save, Cancel, etc.) */
  actions?: React.ReactNode
  /** Avatar fallback text (auto-generated from name if not provided) */
  avatarFallback?: string
  /** Size variant for avatar */
  avatarSize?: 'sm' | 'md' | 'lg'
  /** Enable edit mode with image upload overlays */
  isEditMode?: boolean
  /** Callback when cover image is selected */
  onCoverImageChange?: (file: File) => void
  /** Callback when avatar image is selected */
  onAvatarChange?: (file: File) => void
  /** Whether cover image is uploading */
  isCoverUploading?: boolean
  /** Whether avatar is uploading */
  isAvatarUploading?: boolean
  /** Class name for the content area (avatar, title, badges) - use for max-width constraints */
  contentClassName?: string
}

const avatarSizes = {
  sm: 'size-12',
  md: 'size-16',
  lg: 'size-20',
}

const coverHeights = {
  sm: 'h-[280px]',
  md: 'h-[280px]',
  lg: 'h-[280px]',
}

export const ProfileHeader = React.forwardRef<HTMLDivElement, ProfileHeaderProps>(
  (
    {
      coverImageUrl,
      avatarUrl,
      name,
      subtitle,
      badges,
      actions,
      avatarFallback,
      avatarSize = 'lg',
      isEditMode = false,
      onCoverImageChange,
      onAvatarChange,
      isCoverUploading = false,
      isAvatarUploading = false,
      contentClassName,
      className,
      ...props
    },
    ref
  ) => {
    const fallbackText = avatarFallback ?? getInitials(name)
    const coverInputRef = React.useRef<HTMLInputElement>(null)
    const avatarInputRef = React.useRef<HTMLInputElement>(null)

    const handleCoverClick = () => {
      if (isEditMode && !isCoverUploading) {
        coverInputRef.current?.click()
      }
    }

    const handleAvatarClick = () => {
      if (isEditMode && !isAvatarUploading) {
        avatarInputRef.current?.click()
      }
    }

    const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file && onCoverImageChange) {
        onCoverImageChange(file)
      }
      e.target.value = ''
    }

    const handleAvatarChangeEvent = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file && onAvatarChange) {
        onAvatarChange(file)
      }
      e.target.value = ''
    }

    return (
      <div
        ref={ref}
        className={cn(
          isEditMode
            ? 'md:sticky md:top-[-172px] md:z-20 md:backdrop-blur-md md:bg-background/80 md:pb-1'
            : 'relative',
          className
        )}
        {...props}
      >
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleCoverChange}
        />
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleAvatarChangeEvent}
        />

        <div className="relative rounded-lg overflow-hidden">
          <div
            className={cn(
              'relative bg-linear-to-r from-[#f97316]/20 via-[#f97316]/10 to-transparent overflow-hidden',
              coverHeights[avatarSize]
            )}
          >
            {coverImageUrl && (
              <img src={coverImageUrl} alt="Cover" className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-linear-to-b from-transparent via-transparent to-black/60" />

            {isEditMode && (
              <button
                type="button"
                onClick={handleCoverClick}
                className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
              >
                {isCoverUploading ? (
                  <Loader2 className="size-8 text-white animate-spin" />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-white">
                    <Camera className="size-8" />
                    <span className="text-sm font-medium">Change Cover</span>
                  </div>
                )}
              </button>
            )}

            {/* Actions positioned on cover photo for mobile only */}
            {actions && (
              <div className="absolute top-4 right-4 z-10 items-center gap-2 flex md:hidden!">
                {actions}
              </div>
            )}
          </div>
        </div>

        <div className={cn('relative z-10 -mt-10', contentClassName)}>
          <div className="flex items-end gap-4 px-4">
            <div className="relative">
              <Avatar
                className={cn(avatarSizes[avatarSize], 'border-4 border-background bg-input')}
              >
                {avatarUrl && <AvatarImage src={avatarUrl} alt={name} />}
                <AvatarFallback className="bg-elevated text-xl font-bold text-white">
                  {fallbackText}
                </AvatarFallback>
              </Avatar>

              {isEditMode && (
                <button
                  type="button"
                  onClick={handleAvatarClick}
                  className="absolute inset-0 rounded-full bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center border-4 border-transparent cursor-pointer"
                >
                  {isAvatarUploading ? (
                    <Loader2 className="size-5 text-white animate-spin" />
                  ) : (
                    <Camera className="size-5 text-white" />
                  )}
                </button>
              )}
            </div>

            <div className="flex-1 pb-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold text-white">{name}</h1>
                  {badges}
                </div>
                {actions && <div className="hidden md:flex! items-center gap-2">{actions}</div>}
              </div>
              <p className="text-muted-foreground text-sm mt-0.5">{subtitle ?? <>&nbsp;</>}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

ProfileHeader.displayName = 'ProfileHeader'
